/// <amd-module name="Dashboards/WidgetCatalogUI/WidgetList" />
import * as Resources from "Dashboards/WidgetCatalogUI/Resources";
import React = require("react");

import { WidgetCatalogEntry } from "Dashboards/WidgetContracts/Internal/WidgetCatalogEntry";
import { ObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { FocusZoneContext } from "VSSUI/FocusZone";
import { CustomHeader, HeaderTitleArea } from "VSSUI/Header";
import { Image } from "VSSUI/Image";
import { ILabelModel, LabelGroup, WrappingBehavior } from "VSSUI/Label";
import { Link } from "VSSUI/Link";
import { IListItemDetails, IListRow, List, ListItem, ListSelection } from "VSSUI/List";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

export interface WidgetListProps extends IVssComponentProperties {
    widgetCatalogEntries: WidgetCatalogEntry[];
    selectedItem: WidgetCatalogEntry | undefined;
    searchTextPresent: boolean | undefined;

    /** Fires on selection of an item */
    onSelect: (selectedItem: WidgetCatalogEntry) => void;

    /** Fires when the user double-clicks or hits enter key on an item */
    onActivate: (selectedItem: WidgetCatalogEntry) => void;

    onDrag: (e: MouseEvent, catalogRow: React.RefObject<HTMLDivElement>, selectedItem: WidgetCatalogEntry) => void;
}

/** This panel is used to allow a user to choose a widget. */
export class WidgetList extends VssComponent<WidgetListProps> {
    public render(): JSX.Element | null {
        // Required: widgetCatalogEntries is sorted alphabetically.
        const featureID = "ms.vss-dashboards.dashboard-widget-catalog-categorization-feature";
        const useMultiSelect = false;
        if (this.props.searchTextPresent || !isFeatureEnabled(this.context.pageContext, featureID, false)) {
            const selection = new ListSelection(useMultiSelect);
            if (this.props.selectedItem !== undefined) {
                selection.select(
                    this.props.widgetCatalogEntries.findIndex((item: WidgetCatalogEntry) => {
                        return this.props.selectedItem !== undefined && item.contributionId === this.props.selectedItem.contributionId;
                    })
                );
            }
            return (
                <div>
                    <List
                        key="widget-catalog-list"
                        className="widget-catalog-list"
                        ariaLabel={Resources.WidgetCatalog_Label}
                        itemProvider={new ArrayItemProvider<WidgetCatalogEntry>(this.props.widgetCatalogEntries)}
                        pageSize={100}
                        renderRow={this.renderRow}
                        width="100%"
                        selection={selection}
                        onSelect={this.onSelect}
                        onActivate={this.onActivate}
                    />
                </div>
            );
        }
        const categories = ["Boards", "Repos", "Pipelines", "Test Plans", "Extensions", "General"];
        const categoryList = categories.map((category: string) => {
            const selection = new ListSelection(useMultiSelect);
            const entries = this.categorize(category.toLocaleLowerCase());
            if (this.props.selectedItem !== undefined) {
                selection.select(
                    entries.findIndex((item: WidgetCatalogEntry) => {
                        return this.props.selectedItem !== undefined && item.contributionId === this.props.selectedItem.contributionId;
                    })
                );
            }
            if (entries.length < 1) {
                return;
            }
            return (
                <div>
                    <CustomHeader className="widget-catalog-category ">
                        <HeaderTitleArea>
                            <div className="widget-catalog-category-text">{category}</div>
                        </HeaderTitleArea>
                    </CustomHeader>
                    <List
                        key={`widget-catalog-list-${category}`}
                        className="widget-catalog-list"
                        itemProvider={new ArrayItemProvider<WidgetCatalogEntry>(entries)}
                        pageSize={100}
                        renderRow={this.renderRow}
                        width="100%"
                        selection={selection}
                        onSelect={this.onSelect}
                        onActivate={this.onActivate}
                    />
                </div>
            );
        });
        return <div>{categoryList}</div>;
    }
    private onActivate = (event: React.SyntheticEvent<HTMLElement>, listRow: IListRow<WidgetCatalogEntry>): void => {
        this.props.onActivate(listRow.data);
    };

    private renderRow = (index: number, widgetCatalogEntry: WidgetCatalogEntry, details: IListItemDetails<WidgetCatalogEntry>): JSX.Element => {
        return (
            <ListItem key={"list-item-" + widgetCatalogEntry.contributionId} className="widget-row" details={details} index={index}>
                <FocusZoneContext.Consumer>
                    {cellContext => (
                        <React.Fragment>
                            {
                                <DraggableItemContainer onDrag={this.props.onDrag} content={widgetCatalogEntry}>
                                    <WidgetCatalogEntryItem
                                        widgetCatalogEntry={widgetCatalogEntry}
                                        isSelected={
                                            this.props.selectedItem && widgetCatalogEntry.contributionId === this.props.selectedItem.contributionId
                                        }
                                    />
                                </DraggableItemContainer>
                            }
                        </React.Fragment>
                    )}
                </FocusZoneContext.Consumer>
            </ListItem>
        );
    };

    private onSelect = (event: React.SyntheticEvent<HTMLElement>, listRow: IListRow<WidgetCatalogEntry>): void => {
        this.props.onSelect(listRow.data);
    };

    private categorize = (tag: string): WidgetCatalogEntry[] => {
        const filteredEntries = this.props.widgetCatalogEntries.filter((entry: WidgetCatalogEntry) => {
            if (tag != "extensions") {
                return entry.tags && entry.tags.lastIndexOf(tag) > -1;
            }
            return entry.publisherName.toLocaleLowerCase() != "microsoft";
        });
        return filteredEntries;
    };
}

export interface WidgetCatalogEntryItemProps {
    widgetCatalogEntry: WidgetCatalogEntry;
    isSelected: boolean | undefined;
}
export class WidgetCatalogEntryItem extends VssComponent<WidgetCatalogEntryItemProps> {
    public render(): JSX.Element | null {
        const featureID = "ms.vss-dashboards.dashboard-widget-catalog-categorization-feature";
        const categories = ["boards", "repos", "general", "pipelines", "test plans"];
        const featureEnabled = isFeatureEnabled(this.context.pageContext, featureID, false);
        const labelProps: ObservableArray<ILabelModel> = new ObservableArray<ILabelModel>([]);
        if (this.props.widgetCatalogEntry.tags && featureEnabled) {
            const tags = this.props.widgetCatalogEntry.tags.filter((tag: string) => {
                return categories.lastIndexOf(tag) <= -1;
            });
            labelProps.value = tags.map((tag: string) => {
                return {
                    content: tag,
                    excludeTabStop: true
                };
            });
        }
        if (!featureEnabled) {
            return this.renderLegacy();
        }
        return this.renderFeature(labelProps);
    }

    private createLabels = (labelProps: ObservableArray<ILabelModel>): JSX.Element => {
        return <LabelGroup labelProps={labelProps} wrappingBehavior={WrappingBehavior.oneLine} />;
    };

    private renderLegacy = (): JSX.Element => {
        return (
            <div className="widget-catalog-entry flex flex-noshrink flex-column">
                <div className="flex-row">
                    {this.renderImage()}
                    <div className="widget-details">
                        <div className="widget-name-container">
                            <div className="widget-name fontSizeL text-ellipsis">{this.props.widgetCatalogEntry.name}</div>
                        </div>
                        <div className="widget-description fontSizeM text-ellipsis">{this.props.widgetCatalogEntry.description}</div>
                    </div>
                </div>
                {this.props.isSelected && (
                    <div className="widget-more-info flex-row">
                        <div className="widget-publisher fontSizeM text-ellipsis">
                            {format(Resources.WidgetCatalog_PublisherInfo, this.props.widgetCatalogEntry.publisherName)}
                        </div>
                        <Link className="flagged-widget-gallery-link fontSizeM" target="_blank" href={this.props.widgetCatalogEntry.catalogInfoURI}>
                            {Resources.WidgetCatalog_WidgetLearnMore}
                        </Link>
                    </div>
                )}
            </div>
        );
    };

    private renderFeature = (labelProps: ObservableArray<ILabelModel>): JSX.Element => {
        return (
            <div className="widget-catalog-entry flex flex-noshrink flex-column">
                <div className="flex-row">
                    {this.renderImage()}
                    <div className="widget-details">
                        <div className="widget-name-container">
                            <div className="widget-name fontSizeL text-ellipsis">{this.props.widgetCatalogEntry.name}</div>
                        </div>
                        <div className="widget-publisher fontSizeS text-ellipsis">
                            {format(Resources.WidgetCatalog_PublisherInfo, this.props.widgetCatalogEntry.publisherName)}
                        </div>
                        <div className="widget-description fontSizeM text-ellipsis">
                            {this.props.widgetCatalogEntry.description}
                            {this.props.isSelected && (
                                <Link className="widget-gallery-link" target="_blank" href={this.props.widgetCatalogEntry.catalogInfoURI}>
                                    {" "}
                                    {Resources.WidgetCatalog_WidgetLearnMore}
                                </Link>
                            )}
                            {this.createLabels(labelProps)}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    private renderImage = (): JSX.Element => {
        return (
            <Image
                alt="" // An empty alt should be provided for decorative images. https://www.w3.org/WAI/tutorials/images/decorative/
                className="widget-catalog-image"
                src={this.props.widgetCatalogEntry.catalogIconUrl}
                width={100}
                height={100}
            />
        );
    };
}
export interface DraggableItemContainerProps<T> {
    onDrag: (e: MouseEvent, catalogRow: React.RefObject<HTMLDivElement>, selectedItem: T) => void;
    content: T;
}

export class DraggableItemContainer<T> extends VssComponent<DraggableItemContainerProps<T>> {
    private draggableRef = React.createRef<HTMLDivElement>();

    public render(): JSX.Element | null {
        return (
            <div draggable={true} onDragStart={this.onDragStart} ref={this.draggableRef}>
                {this.props.children}
            </div>
        );
    }

    private onDragStart = (e: any): void => {
        this.handleDrag(e);
    };

    private handleDrag(e: any): void {
        // Hand off drag start event to the CatalogView
        // Note that we only support mouse drag currently
        if (this.props.content != null && e.type === "dragstart") {
            this.props.onDrag(e, this.draggableRef, this.props.content);
        }
    }
}
